import { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { useToast } from 'components/ui/use-toast';
import {
  adminContent,
  type VoiceRuntime,
  type VoiceRuntimeSetting,
} from 'services/api/adminContentClient';

const VoiceRuntimeSettingsCard = () => {
  const { addToast } = useToast();
  const [setting, setSetting] = useState<VoiceRuntimeSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    adminContent.operations.getVoiceRuntime()
      .then(({ data }) => {
        if (isMounted) setSetting(data);
      })
      .catch(() => {
        if (isMounted) {
          addToast({
            title: 'Failed to load Voice Mode version',
            description: 'Please refresh and try again.',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => { isMounted = false; };
  }, [addToast]);

  const updateRuntime = async (runtime: VoiceRuntime) => {
    if (isSaving || !setting || setting.default_runtime === runtime) return;
    setIsSaving(true);
    try {
      const { data } = await adminContent.operations.updateVoiceRuntime(runtime);
      setSetting(data);
      const label = data.runtime_options.find((option) => option.id === data.default_runtime)?.label;
      addToast({
        title: 'Voice Mode version updated',
        description: `New sessions now use ${label ?? data.default_runtime}. Existing sessions keep their version.`,
      });
    } catch {
      addToast({
        title: 'Failed to update Voice Mode version',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="w-full max-w-none" maxWidth="none">
      <CardHeader>
        <CardTitle>Voice Mode version</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 items-stretch">
        <p className="text-sm text-muted-foreground">
          Choose the default version for new module Voice Mode sessions. Existing sessions keep their
          version and saved answers are preserved. Staff can test either version separately with a
          session override.
        </p>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading Voice Mode version...</p>
        ) : setting ? (
          <>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Default Voice Mode version">
              {setting.runtime_options.map((option) => (
                <Button
                  key={option.id}
                  type="button"
                  size="auto"
                  variant={setting.default_runtime === option.id ? 'default' : 'secondary'}
                  aria-pressed={setting.default_runtime === option.id}
                  disabled={isSaving}
                  title={option.description}
                  onClick={() => { void updateRuntime(option.id); }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              {setting.runtime_options.find((option) => option.id === setting.default_runtime)?.description}
            </p>
            {isSaving ? (
              <p className="text-sm text-muted-foreground" role="status">Saving Voice Mode version...</p>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Voice Mode version is unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default VoiceRuntimeSettingsCard;
